import React, { useState, useEffect } from 'react';
import {
  Card,
  CircularProgress,
  Box,
  Chip,
  Typography,
  Divider,
  Pagination,
  TextField,
  InputAdornment,
  Collapse,
} from '@mui/material';
import {
  Category as CategoryIcon,
  Search,
  CheckCircle,
  AttachMoney,
  KeyboardArrowDown,
  KeyboardArrowUp,
  Inventory2Outlined,
} from '@mui/icons-material';
import MDBox from 'components/MDBox';
import MDTypography from 'components/MDTypography';
import { Axios } from 'services/axios';
import { Notify } from 'notiflix';

const ITEMS_PER_PAGE = 8;
const ACCENT = '#667eea';
const ACCENT2 = '#764ba2';

function AddOnsPage() {
  const [subCategories, setSubCategories] = useState([]);
  const [filtered, setFiltered] = useState([]);
  const [loadingPage, setLoadingPage] = useState(false);
  const [expandedIds, setExpandedIds] = useState(new Set());
  const [page, setPage] = useState(1);
  const [searchQuery, setSearchQuery] = useState('');
  const [totalCount, setTotalCount] = useState(0);

  useEffect(() => {
    fetchAdditionalFeatures();
  }, []);

  useEffect(() => {
    const query = searchQuery.toLowerCase().trim();
    const next = query
      ? subCategories.filter(
          (c) =>
            c.subCategoryName?.toLowerCase().includes(query) ||
            c.description?.toLowerCase().includes(query)
        )
      : subCategories;
    setFiltered(next);
    setTotalCount(next.length);
    setPage(1);
    setExpandedIds(new Set());
  }, [searchQuery, subCategories]);

  const fetchAdditionalFeatures = async () => {
    setLoadingPage(true);
    try {
      const response = await Axios.fetch('/shop/additional-features');
      setSubCategories(response.data.data || []);
    } catch (error) {
      console.error('Error fetching additional features:', error);
      Notify.failure(
        error.response?.data?.message || 'Failed to fetch additional features',
      );
    } finally {
      setLoadingPage(false);
    }
  };

  const handleToggle = (id) => {
    setExpandedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
      }
      return next;
    });
  };

  const totalPages = Math.ceil(totalCount / ITEMS_PER_PAGE);
  const paginated = filtered.slice(
    (page - 1) * ITEMS_PER_PAGE,
    page * ITEMS_PER_PAGE
  );
  const globalStartIndex = (page - 1) * ITEMS_PER_PAGE;

  if (loadingPage) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress sx={{ color: ACCENT }} />
      </Box>
    );
  }

  return (
    <MDBox>
      {/* ── Header ── */}
      <Card
        sx={{
          mb: 2.5,
          px: 3,
          py: 1.8,
          background: `linear-gradient(135deg, ${ACCENT} 0%, ${ACCENT2} 100%)`,
          boxShadow: `0 6px 24px rgba(102,126,234,0.4)`,
          borderRadius: 3,
        }}
      >
        <Box display="flex" alignItems="center" justifyContent="space-between" flexWrap="wrap" gap={2}>
          <Box display="flex" alignItems="center" gap={1.5}>
            <Box
              sx={{
                width: 38,
                height: 38,
                borderRadius: 2,
                bgcolor: 'rgba(255,255,255,0.18)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              <Inventory2Outlined sx={{ color: '#fff', fontSize: 20 }} />
            </Box>
            <Box>
              <MDTypography variant="h6" color="white" fontWeight="bold" sx={{ lineHeight: 1.2, letterSpacing: '-0.01em' }}>
                Additional Features
              </MDTypography>
              <MDTypography variant="body2" sx={{ color: 'rgba(255,255,255,0.85)', lineHeight: 1.3, mt: 0.3 }}>
                {totalCount} {totalCount === 1 ? 'category' : 'categories'} — click a row to expand
              </MDTypography>
            </Box>
          </Box>

          <TextField
            size="small"
            placeholder="Search categories..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            sx={{
              width: 248,
              '& .MuiOutlinedInput-root': {
                bgcolor: 'rgba(255,255,255,0.14)',
                borderRadius: 2,
                '& fieldset': { borderColor: 'rgba(255,255,255,0.35)' },
                '&:hover fieldset': { borderColor: 'rgba(255,255,255,0.65)' },
                '&.Mui-focused fieldset': { borderColor: 'rgba(255,255,255,0.9)' },
              },
              '& input': {
                color: '#fff',
                fontSize: 13,
                '&::placeholder': { color: 'rgba(255,255,255,0.65)', opacity: 1 },
              },
            }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <Search sx={{ color: 'rgba(255,255,255,0.75)', fontSize: 18 }} />
                </InputAdornment>
              ),
            }}
          />
        </Box>
      </Card>

      {/* ── Empty state ── */}
      {!paginated.length ? (
        <Card
          sx={{
            p: 7,
            textAlign: 'center',
            border: '1.5px dashed',
            borderColor: 'divider',
            boxShadow: 'none',
            borderRadius: 3,
          }}
        >
          <CategoryIcon sx={{ fontSize: 48, color: 'text.disabled', mb: 1.5 }} />
          <MDTypography variant="h6" color="text.secondary" fontWeight="medium">
            {searchQuery ? 'No categories match your search.' : 'No additional features found.'}
          </MDTypography>
          {searchQuery && (
            <MDTypography variant="caption" color="text.disabled" sx={{ display: 'block', mt: 0.5 }}>
              Try a different keyword
            </MDTypography>
          )}
        </Card>
      ) : (
        <>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.2 }}>
            {paginated.map((subCategory, index) => {
              const isOpen = expandedIds.has(subCategory.id);
              const features = subCategory.features || [];
              const globalIndex = globalStartIndex + index;
              const featureCount = features.length;

              return (
                <Card
                  key={subCategory.id}
                  sx={{
                    overflow: 'hidden',
                    borderRadius: 3,
                    border: '1.5px solid',
                    borderColor: isOpen ? ACCENT : 'divider',
                    borderLeft: isOpen ? `4px solid ${ACCENT}` : '4px solid transparent',
                    boxShadow: isOpen
                      ? `0 4px 20px rgba(102,126,234,0.14)`
                      : '0 1px 4px rgba(0,0,0,0.06)',
                    transition: 'border-color 0.2s, box-shadow 0.2s, border-left-color 0.2s',
                  }}
                >
                  {/* ── SubCategory row ── */}
                  <Box
                    onClick={() => handleToggle(subCategory.id)}
                    sx={{
                      px: 2.5,
                      py: 1.6,
                      display: 'flex',
                      alignItems: 'center',
                      gap: 1.5,
                      cursor: 'pointer',
                      userSelect: 'none',
                      bgcolor: isOpen ? `rgba(102,126,234,0.05)` : 'transparent',
                      '&:hover': { bgcolor: `rgba(102,126,234,0.04)` },
                      transition: 'background 0.18s',
                    }}
                  >
                    {/* Number badge */}
                    <Box
                      sx={{
                        width: 28,
                        height: 28,
                        borderRadius: '50%',
                        flexShrink: 0,
                        background: isOpen
                          ? `linear-gradient(135deg, ${ACCENT}, ${ACCENT2})`
                          : 'rgba(102,126,234,0.1)',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        transition: 'background 0.2s',
                      }}
                    >
                      <Typography
                        sx={{
                          fontSize: 13,
                          fontWeight: 700,
                          color: isOpen ? '#fff' : ACCENT,
                          lineHeight: 1,
                          transition: 'color 0.2s',
                        }}
                      >
                        {globalIndex + 1}
                      </Typography>
                    </Box>

                    {/* Category icon */}
                    <CategoryIcon
                      sx={{
                        fontSize: 20,
                        flexShrink: 0,
                        color: isOpen ? ACCENT : 'text.secondary',
                        transition: 'color 0.2s',
                      }}
                    />

                    {/* Name + description */}
                    <Box flex={1} minWidth={0}>
                      <Typography
                        sx={{
                          fontSize: 15,
                          fontWeight: 700,
                          color: 'text.primary',
                          lineHeight: 1.3,
                          letterSpacing: '-0.01em',
                        }}
                      >
                        {subCategory.subCategoryName}
                      </Typography>
                      {subCategory.description && (
                        <Typography
                          sx={{
                            fontSize: 13,
                            display: 'block',
                            mt: 0.2,
                            color: 'text.secondary',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap',
                            maxWidth: 500,
                            lineHeight: 1.4,
                          }}
                        >
                          {subCategory.description}
                        </Typography>
                      )}
                    </Box>

                    {/* Feature count badge */}
                    <Chip
                      label={`${featureCount} Feature${featureCount !== 1 ? 's' : ''}`}
                      size="small"
                      sx={{
                        height: 26,
                        fontSize: 13,
                        fontWeight: 700,
                        flexShrink: 0,
                        bgcolor: isOpen ? `rgba(102,126,234,0.12)` : 'rgba(0,0,0,0.06)',
                        color: isOpen ? ACCENT : 'text.secondary',
                        border: '1px solid',
                        borderColor: isOpen ? `rgba(102,126,234,0.25)` : 'transparent',
                        transition: 'all 0.2s',
                        '& .MuiChip-label': { px: 1.2 },
                      }}
                    />

                    {/* Expand arrow */}
                    <Box
                      sx={{
                        width: 26,
                        height: 26,
                        borderRadius: '50%',
                        flexShrink: 0,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        bgcolor: isOpen ? `rgba(102,126,234,0.1)` : 'rgba(0,0,0,0.04)',
                        transition: 'background 0.2s',
                      }}
                    >
                      {isOpen
                        ? <KeyboardArrowUp sx={{ fontSize: 18, color: ACCENT }} />
                        : <KeyboardArrowDown sx={{ fontSize: 18, color: 'text.secondary' }} />
                      }
                    </Box>
                  </Box>

                  {/* ── Collapsible features panel ── */}
                  <Collapse in={isOpen} timeout={220}>
                    <Divider sx={{ borderColor: `rgba(102,126,234,0.15)` }} />
                    <Box sx={{ px: 2.5, pt: 1.8, pb: 2 }}>
                      {features.length === 0 ? (
                        <Box
                          sx={{
                            textAlign: 'center',
                            py: 3.5,
                            bgcolor: 'rgba(0,0,0,0.02)',
                            borderRadius: 2,
                            border: '1px dashed',
                            borderColor: 'divider',
                          }}
                        >
                          <MDTypography variant="body2" color="text.secondary">
                            No features available in this category.
                          </MDTypography>
                        </Box>
                      ) : (
                        <>
                          {/* Scroll hint label */}
                          <Box
                            display="flex"
                            alignItems="center"
                            justifyContent="space-between"
                            mb={1}
                          >
                            <Typography
                              sx={{
                                fontSize: 12,
                                fontWeight: 700,
                                textTransform: 'uppercase',
                                letterSpacing: '0.07em',
                                color: 'text.disabled',
                              }}
                            >
                              Features
                            </Typography>
                            {features.length > 4 && (
                              <Typography
                                sx={{
                                  fontSize: 12,
                                  color: ACCENT,
                                  fontWeight: 600,
                                  opacity: 0.8,
                                }}
                              >
                                Scroll to see all {features.length}
                              </Typography>
                            )}
                          </Box>

                          {/* Feature list */}
                          <Box
                            sx={{
                              ...(features.length > 4 && {
                                maxHeight: 258,
                                overflowY: 'auto',
                                pr: 0.5,
                                '&::-webkit-scrollbar': { width: 5 },
                                '&::-webkit-scrollbar-track': {
                                  borderRadius: 3,
                                  bgcolor: 'rgba(0,0,0,0.04)',
                                },
                                '&::-webkit-scrollbar-thumb': {
                                  borderRadius: 3,
                                  bgcolor: `rgba(102,126,234,0.3)`,
                                  '&:hover': { bgcolor: `rgba(102,126,234,0.55)` },
                                },
                              }),
                              display: 'flex',
                              flexDirection: 'column',
                              gap: 0.7,
                            }}
                          >
                            {features.map((feature, fi) => (
                              <Box
                                key={feature.id}
                                sx={{
                                  px: 1.8,
                                  py: 1,
                                  display: 'flex',
                                  alignItems: 'center',
                                  gap: 1.4,
                                  border: '1px solid',
                                  borderColor: 'divider',
                                  borderRadius: 2,
                                  transition: 'all 0.18s',
                                  bgcolor: 'transparent',
                                  '&:hover': {
                                    borderColor: `rgba(102,126,234,0.4)`,
                                    bgcolor: `rgba(102,126,234,0.03)`,
                                    boxShadow: `0 2px 8px rgba(102,126,234,0.08)`,
                                  },
                                }}
                              >
                                {/* Row number */}
                                <Typography
                                  sx={{
                                    fontSize: 12,
                                    fontWeight: 700,
                                    color: 'text.disabled',
                                    flexShrink: 0,
                                    width: 18,
                                    textAlign: 'center',
                                    lineHeight: 1,
                                  }}
                                >
                                  {fi + 1}
                                </Typography>

                                <CheckCircle sx={{ fontSize: 16, color: '#4caf50', flexShrink: 0 }} />

                                {/* Name + description */}
                                <Box flex={1} minWidth={0}>
                                  <Typography
                                    sx={{
                                      fontSize: 14,
                                      fontWeight: 600,
                                      color: 'text.primary',
                                      lineHeight: 1.3,
                                      letterSpacing: '-0.005em',
                                    }}
                                  >
                                    {feature.name}
                                  </Typography>
                                  {feature.description && (
                                    <Typography
                                      sx={{
                                        fontSize: 13,
                                        color: 'text.secondary',
                                        display: 'block',
                                        mt: 0.2,
                                        lineHeight: 1.4,
                                        overflow: 'hidden',
                                        textOverflow: 'ellipsis',
                                        whiteSpace: 'nowrap',
                                      }}
                                    >
                                      {feature.description}
                                    </Typography>
                                  )}
                                </Box>

                                {/* Right side: price + status */}
                                <Box display="flex" alignItems="center" gap={0.8} flexShrink={0}>
                                  {feature.featureDefaultPrice != null && (
                                    <Box
                                      display="flex"
                                      alignItems="center"
                                      gap={0.3}
                                      sx={{
                                        bgcolor: `rgba(102,126,234,0.08)`,
                                        border: `1px solid rgba(102,126,234,0.2)`,
                                        borderRadius: 5,
                                        px: 1,
                                        py: 0.3,
                                      }}
                                    >
                                      <AttachMoney sx={{ fontSize: 14, color: ACCENT }} />
                                      <Typography
                                        sx={{ fontSize: 13, fontWeight: 700, color: ACCENT, lineHeight: 1 }}
                                      >
                                        {feature.featureDefaultPrice}
                                      </Typography>
                                    </Box>
                                  )}
                                  {feature.active === false && (
                                    <Chip
                                      label="Inactive"
                                      size="small"
                                      sx={{
                                        height: 22,
                                        fontSize: 12,
                                        bgcolor: 'rgba(0,0,0,0.06)',
                                        color: 'text.disabled',
                                        '& .MuiChip-label': { px: 1 },
                                      }}
                                    />
                                  )}
                                </Box>
                              </Box>
                            ))}
                          </Box>
                        </>
                      )}
                    </Box>
                  </Collapse>
                </Card>
              );
            })}
          </Box>

          {/* ── Pagination ── */}
          {totalPages > 1 && (
            <Box
              display="flex"
              justifyContent="space-between"
              alignItems="center"
              mt={2.5}
              flexWrap="wrap"
              gap={2}
            >
              <Typography sx={{ fontSize: 14, color: 'text.secondary' }}>
                Showing {globalStartIndex + 1}–{Math.min(globalStartIndex + ITEMS_PER_PAGE, totalCount)} of {totalCount} categories
              </Typography>
              <Pagination
                count={totalPages}
                page={page}
                onChange={(_, value) => {
                  setPage(value);
                  setExpandedIds(new Set());
                  window.scrollTo({ top: 0, behavior: 'smooth' });
                }}
                shape="rounded"
                size="medium"
                sx={{
                  '& .MuiPaginationItem-root': {
                    fontSize: 13,
                    borderRadius: 1.5,
                  },
                  '& .MuiPaginationItem-root.Mui-selected': {
                    background: `linear-gradient(135deg, ${ACCENT}, ${ACCENT2})`,
                    color: '#fff',
                    border: 'none',
                    '&:hover': { opacity: 0.9 },
                  },
                }}
              />
            </Box>
          )}
        </>
      )}
    </MDBox>
  );
}

export default AddOnsPage;
